import { AnimatePresence, motion } from 'framer-motion';
import {
  Activity,
  AlertCircle,
  Brain,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  Expand,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Clock,
  Eye,
  Globe,
  Image,
  List,
  Loader2,
  MousePointerClick,
  Sparkles,
  Target,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { cn } from '@/lib/utils';
import type {
  BrowserUseScraperRunDetail,
  BrowserUseScraperStep,
} from '@/services/api/browserUseScraperService';
import {
  useGetScraperRunByWorkflowIdQuery,
} from '@/services/api/browserUseScraperService';

import { Badge } from '../ui/badge';

const POLL_INTERVAL_MS = 2000;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

export const StatusPill: React.FC<{ status: string; successful?: boolean | null }> = ({
  status,
  successful,
}) => {
  const config = useMemo(() => {
    if (status === 'running')
      return {
        label: 'Running',
        className: 'bg-blue-500/10 text-blue-600 border-blue-500/20 dark:text-blue-400',
        icon: <Loader2 className="h-3 w-3 animate-spin" />,
      };
    if (status === 'completed' && successful)
      return {
        label: 'Completed',
        className: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20 dark:text-emerald-400',
        icon: <CheckCircle2 className="h-3 w-3" />,
      };
    if (status === 'failed' || (status === 'completed' && successful === false))
      return {
        label: 'Failed',
        className: 'bg-red-500/10 text-red-600 border-red-500/20 dark:text-red-400',
        icon: <AlertCircle className="h-3 w-3" />,
      };
    return {
      label: 'Pending',
      className: 'bg-muted text-muted-foreground border-border',
      icon: <Clock className="h-3 w-3" />,
    };
  }, [status, successful]);

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium',
        config.className,
      )}
    >
      {config.icon}
      {config.label}
    </span>
  );
};

/**
 * Extract a meaningful display path from a URL, handling hash-based routing
 * (e.g. `https://app.example.com/#/project/123/activity` → `/project/123/activity`).
 */
function displayPath(raw: string): string {
  try {
    const u = new URL(raw);
    // Hash-routed SPAs put the real path after #
    if (u.hash && u.hash.length > 1) {
      return u.hash.slice(1); // strip leading #
    }
    const p = u.pathname + u.search;
    return p === '/' ? u.host : p;
  } catch {
    return raw;
  }
}

/** A contiguous group of steps sharing the same URL. */
interface UrlSegment {
  url: string;
  startIndex: number;
  endIndex: number; // inclusive
}

/** Palette of subtle tints used to alternate URL segment backgrounds. */
const SEGMENT_COLORS = [
  'bg-blue-500/8',
  'bg-violet-500/8',
  'bg-cyan-500/8',
];

function buildUrlSegments(steps: BrowserUseScraperStep[]): UrlSegment[] {
  if (steps.length === 0) return [];
  const segments: UrlSegment[] = [];
  let current: UrlSegment = { url: steps[0].url ?? '', startIndex: 0, endIndex: 0 };
  for (let i = 1; i < steps.length; i += 1) {
    const stepUrl = steps[i].url ?? '';
    if (stepUrl === current.url) {
      current.endIndex = i;
    } else {
      segments.push(current);
      current = { url: stepUrl, startIndex: i, endIndex: i };
    }
  }
  segments.push(current);
  return segments;
}

const StepScrubber: React.FC<{
  steps: BrowserUseScraperStep[];
  totalSteps: number;
  selectedIndex: number;
  onSelect: (index: number) => void;
  isLive: boolean;
}> = ({ steps, totalSteps, selectedIndex, onSelect, isLive }) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const urlSegments = useMemo(() => buildUrlSegments(steps), [steps]);

  const indexFromClientX = useCallback(
    (clientX: number): number => {
      if (totalSteps === 0 || !trackRef.current) return 0;
      const rect = trackRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
      return Math.min(Math.floor((x / rect.width) * totalSteps), totalSteps - 1);
    },
    [totalSteps],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (totalSteps === 0 || !trackRef.current) return;
      onSelect(indexFromClientX(e.clientX));
    },
    [totalSteps, onSelect, indexFromClientX],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (totalSteps === 0 || !trackRef.current) return;
      setHoverIndex(indexFromClientX(e.clientX));
    },
    [totalSteps, indexFromClientX],
  );

  const handleMouseLeave = useCallback(() => {
    setHoverIndex(null);
  }, []);

  // --- Mouse drag ---
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (totalSteps === 0) return;
      e.preventDefault();
      isDragging.current = true;
      onSelect(indexFromClientX(e.clientX));

      const handleDragMove = (ev: MouseEvent) => {
        if (!isDragging.current) return;
        onSelect(indexFromClientX(ev.clientX));
      };
      const handleMouseUp = () => {
        isDragging.current = false;
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleMouseUp);
    },
    [totalSteps, onSelect, indexFromClientX],
  );

  // --- Touch drag ---
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (totalSteps === 0) return;
      isDragging.current = true;
      const touch = e.touches[0];
      if (touch) onSelect(indexFromClientX(touch.clientX));

      const handleTouchMove = (ev: TouchEvent) => {
        if (!isDragging.current) return;
        const t = ev.touches[0];
        if (t) onSelect(indexFromClientX(t.clientX));
      };
      const handleTouchEnd = () => {
        isDragging.current = false;
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleTouchEnd);
      };
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleTouchEnd);
    },
    [totalSteps, onSelect, indexFromClientX],
  );

  if (totalSteps === 0) {
    return <div className="h-1.5 w-full rounded-full bg-muted" />;
  }

  const thumbPct = (selectedIndex / Math.max(totalSteps - 1, 1)) * 100;
  const fillPct = thumbPct;

  const canGoBack = selectedIndex > 0;
  const canGoForward = selectedIndex < totalSteps - 1;

  // Find the last step index of the previous / next URL segment
  const currentSegmentIdx = urlSegments.findIndex(
    (seg) => selectedIndex >= seg.startIndex && selectedIndex <= seg.endIndex,
  );
  const prevSegmentLastIndex =
    currentSegmentIdx > 0 ? urlSegments[currentSegmentIdx - 1].endIndex : null;
  const nextSegmentLastIndex =
    currentSegmentIdx < urlSegments.length - 1
      ? urlSegments[currentSegmentIdx + 1].endIndex
      : null;

  const btnClass =
    'flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-muted-foreground';

  return (
    <div className="group relative flex items-center gap-2 py-2">
      <button
        type="button"
        onClick={() => prevSegmentLastIndex != null && onSelect(prevSegmentLastIndex)}
        disabled={prevSegmentLastIndex == null}
        className={btnClass}
        title="Previous page"
      >
        <ChevronsLeft className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => canGoBack && onSelect(selectedIndex - 1)}
        disabled={!canGoBack}
        className={btnClass}
      >
        <ChevronLeft className="h-4 w-4" />
      </button>

      <div
        ref={trackRef}
        role="slider"
        tabIndex={0}
        aria-valuenow={selectedIndex}
        aria-valuemin={0}
        aria-valuemax={totalSteps - 1}
        aria-label="Step scrubber"
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight' && selectedIndex < totalSteps - 1) onSelect(selectedIndex + 1);
          if (e.key === 'ArrowLeft' && selectedIndex > 0) onSelect(selectedIndex - 1);
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        className="relative h-1.5 w-full cursor-pointer rounded-full bg-muted before:absolute before:-inset-y-2 before:inset-x-0 before:content-['']"
      >
        {/* URL segment backgrounds */}
        {urlSegments.map((seg, segIdx) => {
          const leftPct = (seg.startIndex / Math.max(totalSteps - 1, 1)) * 100;
          const rightPct = (seg.endIndex / Math.max(totalSteps - 1, 1)) * 100;
          return (
            <div
              key={segIdx}
              className={cn(
                'absolute inset-y-0 rounded-full',
                SEGMENT_COLORS[segIdx % SEGMENT_COLORS.length],
              )}
              style={{
                left: `${leftPct}%`,
                width: `${rightPct - leftPct}%`,
              }}
            />
          );
        })}

        {/* Fill */}
        <motion.div
          className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-blue-500 to-violet-500"
          animate={{ width: `${fillPct}%` }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        />

        {/* URL change dividers */}
        {urlSegments.slice(1).map((seg) => (
          <div
            key={`div-${seg.startIndex}`}
            className="absolute top-1/2 h-4 w-0.5 -translate-y-1/2 rounded-full bg-foreground/25"
            style={{ left: `${(seg.startIndex / Math.max(totalSteps - 1, 1)) * 100}%` }}
          />
        ))}

        {/* Step tick marks */}
        {totalSteps <= 60 &&
          Array.from({ length: totalSteps }).map((_, i) => (
            <div
              key={i}
              className="absolute top-1/2 h-2.5 w-px -translate-y-1/2 bg-border/40"
              style={{ left: `${(i / Math.max(totalSteps - 1, 1)) * 100}%` }}
            />
          ))}

        {/* Hover tooltip showing URL */}
        {hoverIndex !== null && (() => {
          const hoveredStep = steps[hoverIndex];
          const hoveredUrl = hoveredStep?.url;
          if (!hoveredUrl) return null;
          const pathname = displayPath(hoveredUrl);
          const hoverPct = (hoverIndex / Math.max(totalSteps - 1, 1)) * 100;
          return (
            <div
              className="pointer-events-none absolute -top-9 z-20 -translate-x-1/2 whitespace-nowrap rounded-md bg-popover px-2 py-1 text-[10px] font-mono text-popover-foreground shadow-md border"
              style={{ left: `${hoverPct}%` }}
            >
              {pathname.length > 40 ? `${pathname.slice(0, 37)}…` : pathname}
            </div>
          );
        })()}

        {/* Thumb */}
        <motion.div
          className={cn(
            'absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-background shadow-md transition-transform cursor-grab active:cursor-grabbing',
            'bg-gradient-to-br from-blue-500 to-violet-500',
            'group-hover:scale-110',
          )}
          animate={{ left: `${thumbPct}%` }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        />
      </div>

      <button
        type="button"
        onClick={() => canGoForward && onSelect(selectedIndex + 1)}
        disabled={!canGoForward}
        className={btnClass}
      >
        <ChevronRight className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => nextSegmentLastIndex != null && onSelect(nextSegmentLastIndex)}
        disabled={nextSegmentLastIndex == null}
        className={btnClass}
        title="Next page"
      >
        <ChevronsRight className="h-4 w-4" />
      </button>

      {/* Step label */}
      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
        {selectedIndex + 1} / {totalSteps}
      </span>

      {/* Jump to live */}
      {isLive && selectedIndex !== totalSteps - 1 && (
        <motion.button
          initial={{ opacity: 0, x: 4 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 4 }}
          onClick={() => onSelect(totalSteps - 1)}
          className="ml-2 flex shrink-0 items-center gap-1 rounded-md bg-blue-600 px-2 py-0.5 text-[11px] font-medium text-white transition hover:bg-blue-500"
        >
          <Activity className="h-3 w-3" />
          Live
        </motion.button>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Screenshot viewer
// ---------------------------------------------------------------------------

/**
 * Extract a stable cache key from a pre-signed S3 URL by stripping query params.
 * This prevents React from re-mounting the <img> on every poll when only the
 * signature/expiry changes.
 */
function stableImageKey(url: string): string {
  try {
    const u = new URL(url);
    return `${u.origin}${u.pathname}`;
  } catch {
    return url;
  }
}

export const ScreenshotViewer: React.FC<{
  step: BrowserUseScraperStep | null;
  isLive: boolean;
  /** When provided, shows a pop-out button in the top-right corner */
  onPopOut?: () => void;
}> = ({ step, isLive, onPopOut }) => {
  const url = step?.screenshot_url;
  const imageKey = useMemo(() => (url ? stableImageKey(url) : null), [url]);

  // Track the last loaded aspect ratio so the container never collapses to 0
  // height while a new image is loading.
  const [aspectRatio, setAspectRatio] = useState<string | undefined>(undefined);

  const handleImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    if (img.naturalWidth && img.naturalHeight) {
      setAspectRatio(`${img.naturalWidth} / ${img.naturalHeight}`);
    }
  }, []);

  const hasImage = !!(url && imageKey);

  return (
    <div
      className="relative flex w-full items-center justify-center overflow-hidden rounded-xl border bg-muted/30"
      style={{ aspectRatio, minHeight: aspectRatio ? undefined : '200px', maxHeight: '100%' }}
    >
      {/* Live indicator */}
      {isLive && (
        <div className="absolute left-3 top-3 z-10 flex items-center gap-1.5">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
          </span>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-red-500 dark:text-red-400">
            Live
          </span>
        </div>
      )}

      {/* Top-right: step badge + pop-out */}
      <div className="absolute right-3 top-3 z-10 flex items-center gap-1.5">
        {step && (
          <Badge variant="secondary" className="backdrop-blur-sm">
            Step {step.step_number}
          </Badge>
        )}
        {onPopOut && (
          <button
            type="button"
            onClick={onPopOut}
            className="flex items-center gap-1 rounded-md bg-black/50 backdrop-blur-sm px-2 py-1 text-[10px] font-medium text-white hover:bg-black/70 transition-colors"
            title="Open in larger view"
          >
            <Expand className="h-3 w-3" />
          </button>
        )}
      </div>

      <AnimatePresence mode="popLayout">
        {hasImage ? (
          <motion.img
            key={imageKey}
            src={url}
            alt={`Step ${step?.step_number ?? '?'} screenshot`}
            className="absolute inset-0 h-full w-full object-contain"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            onLoad={handleImageLoad}
          />
        ) : (
          <motion.div
            key="placeholder"
            className="flex flex-col items-center gap-3 text-muted-foreground"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Eye className="h-12 w-12 stroke-[1.5]" />
            <span className="text-sm font-medium">
              {step ? 'No screenshot available' : 'Select a step to preview'}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// TimelineScrubber removed — replaced by StepScrubber above the screenshot

// ---------------------------------------------------------------------------
// Step detail panel
// ---------------------------------------------------------------------------

const StepDetailCard: React.FC<{
  step: BrowserUseScraperStep;
  isLatest: boolean;
}> = ({ step, isLatest }) => {
  const [expanded, setExpanded] = useState(false);

  const sections = useMemo(() => {
    const s: { icon: React.ReactNode; label: string; content: string; accent?: string }[] = [];

    if (step.thinking) {
      s.push({
        icon: <Brain className="h-3.5 w-3.5" />,
        label: 'Thinking',
        content: step.thinking,
        accent: 'text-violet-600 dark:text-violet-400',
      });
    }
    if (step.memory) {
      s.push({
        icon: <Sparkles className="h-3.5 w-3.5" />,
        label: 'Memory',
        content: step.memory,
        accent: 'text-amber-600 dark:text-amber-400',
      });
    }
    if (step.next_goal) {
      s.push({
        icon: <Target className="h-3.5 w-3.5" />,
        label: 'Next Goal',
        content: step.next_goal,
        accent: 'text-blue-600 dark:text-blue-400',
      });
    }
    if (step.extracted_content) {
      s.push({
        icon: <MousePointerClick className="h-3.5 w-3.5" />,
        label: 'Extracted',
        content: step.extracted_content,
        accent: 'text-emerald-600 dark:text-emerald-400',
      });
    }
    return s;
  }, [step]);

  const actionSummary = useMemo(() => {
    return step.actions
      .map((a) => {
        const keys = Object.keys(a);
        return keys[0] ?? 'unknown';
      })
      .join(', ');
  }, [step.actions]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        'group rounded-lg border transition-colors duration-200',
        isLatest
          ? 'border-blue-500/30 bg-blue-500/5'
          : 'bg-card hover:border-border/80',
      )}
    >
      {/* Header */}
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left"
      >
        {/* Step number */}
        <div
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold',
            step.is_done
              ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
              : isLatest
                ? 'bg-blue-500/15 text-blue-600 dark:text-blue-400'
                : 'bg-muted text-muted-foreground',
          )}
        >
          {step.is_done ? <CheckCircle2 className="h-3.5 w-3.5" /> : step.step_number}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium text-foreground">
              {step.title || `Step ${step.step_number}`}
            </span>
            {step.duration_seconds != null && (
              <span className="shrink-0 text-[11px] text-muted-foreground/60">
                {step.duration_seconds.toFixed(1)}s
              </span>
            )}
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
            {step.url && (
              <span className="flex items-center gap-1 truncate">
                <Globe className="h-3 w-3 shrink-0" />
                {new URL(step.url).pathname}
              </span>
            )}
            {actionSummary && (
              <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono">
                {actionSummary}
              </span>
            )}
          </div>
        </div>

        <motion.div
          animate={{ rotate: expanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </motion.div>
      </button>

      {/* Expandable detail */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="space-y-3 border-t px-4 py-3">
              {sections.map((section) => (
                <div key={section.label} className="space-y-1">
                  <div className={cn('flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider', section.accent)}>
                    {section.icon}
                    {section.label}
                  </div>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {section.content}
                  </p>
                </div>
              ))}

              {step.plan_update && step.plan_update.length > 0 && (
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-cyan-600 dark:text-cyan-400">
                    <Activity className="h-3.5 w-3.5" />
                    Plan
                  </div>
                  <ol className="space-y-0.5 pl-4 text-sm text-muted-foreground">
                    {step.plan_update.map((item, i) => (
                      <li
                        key={i}
                        className={cn(
                          'list-decimal',
                          step.current_plan_item === i && 'font-medium text-cyan-600 dark:text-cyan-400',
                        )}
                      >
                        {item}
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {step.actions.length > 0 && (
                <details className="group/raw">
                  <summary className="cursor-pointer text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60 transition hover:text-muted-foreground">
                    Raw Actions &amp; Results
                  </summary>
                  <pre className="mt-2 max-h-48 overflow-auto rounded-md bg-muted p-3 text-[11px] leading-relaxed text-muted-foreground">
                    {JSON.stringify({ actions: step.actions, results: step.results }, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ---------------------------------------------------------------------------
// Main viewer
// ---------------------------------------------------------------------------

interface BrowserAgentViewerProps {
  workflowId: string;
  /** When true, forces stacked layout (screenshot above activity). */
  compact?: boolean;
  /** When true, hides the header bar (useful when a parent already shows run info). */
  hideHeader?: boolean;
  /** When set, snap to the latest step matching this URL. */
  snapToUrl?: string | null;
  /** When provided, shows a pop-out button on the screenshot viewer. */
  onPopOut?: () => void;
}

type CompactTab = 'screenshot' | 'activity';

export const BrowserAgentViewer: React.FC<BrowserAgentViewerProps> = ({ workflowId, compact = false, hideHeader = false, snapToUrl, onPopOut }) => {
  const [selectedStepIndex, setSelectedStepIndex] = useState<number>(-1);
  const [userPinnedStep, setUserPinnedStep] = useState(false);
  const [compactTab, setCompactTab] = useState<CompactTab>('screenshot');
  const stepsEndRef = useRef<HTMLDivElement>(null);

  const { data: run, isLoading, isError, error } = useGetScraperRunByWorkflowIdQuery(workflowId, {
    pollingInterval: POLL_INTERVAL_MS,
    skip: !workflowId,
  });

  const isLive = run?.status === 'running' || run?.status === 'pending';
  const steps = run?.steps ?? [];

  // Auto-follow latest step when live and user hasn't pinned
  useEffect(() => {
    if (steps.length > 0 && !userPinnedStep) {
      setSelectedStepIndex(steps.length - 1);
    }
  }, [steps.length, userPinnedStep]);

  // Auto-scroll step list when live
  useEffect(() => {
    if (isLive && !userPinnedStep && stepsEndRef.current) {
      stepsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [steps.length, isLive, userPinnedStep]);

  // Snap to URL: find the latest step whose URL matches and select it
  useEffect(() => {
    if (!snapToUrl || steps.length === 0) return;
    // Find last step matching this URL (latest is most relevant)
    for (let i = steps.length - 1; i >= 0; i -= 1) {
      if (steps[i].url && steps[i].url === snapToUrl) {
        setSelectedStepIndex(i);
        setUserPinnedStep(true);
        return;
      }
    }
    // Fallback: partial match (URL starts with or contains the snap target)
    for (let i = steps.length - 1; i >= 0; i -= 1) {
      if (steps[i].url && snapToUrl && steps[i].url!.includes(snapToUrl)) {
        setSelectedStepIndex(i);
        setUserPinnedStep(true);
        return;
      }
    }
  }, [snapToUrl, steps]);

  const handleSelectStep = useCallback(
    (index: number) => {
      setSelectedStepIndex(index);
      const isLatest = index === steps.length - 1;
      setUserPinnedStep(!isLatest);
    },
    [steps.length],
  );

  // In compact mode, clicking a step in the activity list switches to screenshot tab
  const handleActivityStepClick = useCallback(
    (index: number) => {
      handleSelectStep(index);
      if (compact) setCompactTab('screenshot');
    },
    [handleSelectStep, compact],
  );

  const selectedStep = steps[selectedStepIndex] ?? null;

  if (isLoading) {
    return (
      <div className="flex h-[600px] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Loading agent run…</span>
        </div>
      </div>
    );
  }

  if (isError || !run) {
    return (
      <div className="flex h-[600px] items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-center">
          <AlertCircle className="h-8 w-8 text-destructive" />
          <span className="text-sm font-medium text-foreground">Failed to load run</span>
          <span className="max-w-sm text-xs text-muted-foreground">
            {(error as { data?: { error?: string } })?.data?.error ?? 'Run not found or network error'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-0 overflow-hidden rounded-2xl border bg-background shadow-sm">
      {/* ── Header ─────────────────────────────────────────────── */}
      {!hideHeader && (
        <div className="flex items-center justify-between border-b px-5 py-3">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-violet-600 to-blue-600">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-foreground">Browser Agent</h2>
              <p className="text-[11px] text-muted-foreground font-mono">{run.workflow_id}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">
              {steps.length} step{steps.length !== 1 ? 's' : ''}
            </span>
            <StatusPill status={run.status} successful={run.successful} />
          </div>
        </div>
      )}

      {/* ── Main content ───────────────────────────────────────── */}
      {compact ? (
        /* ── Compact: tabbed layout ──────────────────────────── */
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {/* Content area — switches between screenshot and activity */}
          <div className="min-h-0 flex-1 overflow-hidden">
            {compactTab === 'screenshot' ? (
              <div className="flex h-full flex-col">
                <div className="flex-1 p-3">
                  <ScreenshotViewer step={selectedStep} isLive={isLive && !userPinnedStep} onPopOut={onPopOut} />
                </div>
              </div>
            ) : (
              <div className="flex h-full flex-col">
                <div className="min-h-0 flex-1 overflow-y-auto">
                  <div className="space-y-2 p-3">
                    <AnimatePresence initial={false}>
                      {steps.map((step, idx) => (
                        <div key={step.id} role="button" tabIndex={0} onClick={() => handleActivityStepClick(idx)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleActivityStepClick(idx); } }} className="cursor-pointer">
                          <StepDetailCard
                            step={step}
                            isLatest={idx === steps.length - 1 && isLive}
                          />
                        </div>
                      ))}
                    </AnimatePresence>

                    {isLive && steps.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="flex items-center justify-center gap-2 py-4 text-xs text-muted-foreground"
                      >
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Waiting for next step…
                      </motion.div>
                    )}

                    <div ref={stepsEndRef} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Scrubber — always visible */}
          <div className="border-t px-4">
            <StepScrubber
              steps={steps}
              totalSteps={steps.length}
              selectedIndex={selectedStepIndex}
              onSelect={handleSelectStep}
              isLive={isLive}
            />
            {selectedStep?.url && (
              <div className="flex items-center gap-1.5 pb-2 text-[11px] font-mono text-muted-foreground truncate">
                <Globe className="h-3 w-3 shrink-0" />
                <span className="truncate">{displayPath(selectedStep.url)}</span>
              </div>
            )}
          </div>

          {/* Tab bar */}
          <div className="flex items-center border-t">
            <button
              type="button"
              onClick={() => setCompactTab('screenshot')}
              className={cn(
                'flex flex-1 items-center justify-center gap-1.5 py-2 text-xs font-medium transition-colors',
                compactTab === 'screenshot'
                  ? 'text-foreground border-b-2 border-blue-500'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Image className="h-3.5 w-3.5" />
              Screenshot
            </button>
            <button
              type="button"
              onClick={() => setCompactTab('activity')}
              className={cn(
                'flex flex-1 items-center justify-center gap-1.5 py-2 text-xs font-medium transition-colors',
                compactTab === 'activity'
                  ? 'text-foreground border-b-2 border-blue-500'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <List className="h-3.5 w-3.5" />
              Activity
              {steps.length > 0 && (
                <span className="text-[10px] text-muted-foreground/60">{steps.length}</span>
              )}
            </button>
          </div>

          {/* Footers */}
          {run.error_message && (
            <div className="border-t border-red-500/20 bg-red-500/5 px-4 py-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                <p className="text-xs leading-relaxed text-red-600 dark:text-red-300">{run.error_message}</p>
              </div>
            </div>
          )}
          {run.successful && run.structured_output && (
            <div className="border-t border-emerald-500/20 bg-emerald-500/5 px-4 py-3">
              <details>
                <summary className="flex cursor-pointer items-center gap-2 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Structured Output
                  <ChevronDown className="ml-auto h-3 w-3" />
                </summary>
                <pre className="mt-2 max-h-48 overflow-auto rounded-md bg-muted p-3 text-[11px] leading-relaxed text-muted-foreground">
                  {JSON.stringify(run.structured_output, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </div>
      ) : (
        /* ── Full-width: side-by-side layout ─────────────────── */
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
          {/* Screenshot panel */}
          <div className="flex min-h-0 flex-col overflow-hidden border-b lg:w-3/5 lg:border-b-0 lg:border-r">
            <div className="min-h-0 flex-1 overflow-hidden p-3">
              <ScreenshotViewer step={selectedStep} isLive={isLive && !userPinnedStep} onPopOut={onPopOut} />
            </div>

            {/* Step scrubber */}
            <div className="border-t px-4">
              <StepScrubber
                steps={steps}
                totalSteps={steps.length}
                selectedIndex={selectedStepIndex}
                onSelect={handleSelectStep}
                isLive={isLive}
              />
              {selectedStep?.url && (
                <div className="flex items-center gap-1.5 pb-2 text-[11px] font-mono text-muted-foreground truncate">
                  <Globe className="h-3 w-3 shrink-0" />
                  <span className="truncate">{displayPath(selectedStep.url)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Step activity feed */}
          <div className="flex min-h-0 flex-1 flex-col lg:w-2/5">
            <div className="flex items-center justify-between border-b px-4 py-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Activity
              </span>
              {steps.length > 0 && (
                <span className="text-[11px] text-muted-foreground/60">
                  {steps.length} step{steps.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              <div className="space-y-2 p-3">
                <AnimatePresence initial={false}>
                  {steps.map((step, idx) => (
                    <div key={step.id} role="button" tabIndex={0} onClick={() => handleSelectStep(idx)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSelectStep(idx); } }} className="cursor-pointer">
                      <StepDetailCard
                        step={step}
                        isLatest={idx === steps.length - 1 && isLive}
                      />
                    </div>
                  ))}
                </AnimatePresence>

                {isLive && steps.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex items-center justify-center gap-2 py-4 text-xs text-muted-foreground"
                  >
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Waiting for next step…
                  </motion.div>
                )}

                <div ref={stepsEndRef} />
              </div>
            </div>

            {/* Error footer */}
            {run.error_message && (
              <div className="border-t border-red-500/20 bg-red-500/5 px-4 py-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                  <p className="text-xs leading-relaxed text-red-600 dark:text-red-300">{run.error_message}</p>
                </div>
              </div>
            )}

            {/* Success footer with structured output */}
            {run.successful && run.structured_output && (
              <div className="border-t border-emerald-500/20 bg-emerald-500/5 px-4 py-3">
                <details>
                  <summary className="flex cursor-pointer items-center gap-2 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Structured Output
                    <ChevronDown className="ml-auto h-3 w-3" />
                  </summary>
                  <pre className="mt-2 max-h-48 overflow-auto rounded-md bg-muted p-3 text-[11px] leading-relaxed text-muted-foreground">
                    {JSON.stringify(run.structured_output, null, 2)}
                  </pre>
                </details>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
